import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@/lib/api';
import { impersonateUser } from '@/lib/users-api';
import { writeLocaleCookie, type Locale } from '@/lib/i18n';
import type { User, LoginCredentials, ApiResponse, ForgotPasswordRequest, ResetPasswordRequest, OtpLoginResponse, LoginSuccessResponse } from '@/types';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /** /auth/me unreachable (offline, timeout, 5xx) — the token is kept. */
  authUnreachable: boolean;
  _hasHydrated: boolean;
  otpSession: string | null;
  otpEmail: string | null;
  /**
   * Impersonation — the admin's OWN token, stashed while acting as another user.
   * Persisted (and mirrored to localStorage['original_token']) so a page reload
   * mid-impersonation can still be unwound. `isImpersonating` is a plain field
   * kept in sync with `originalToken` rather than a getter: zustand selectors
   * can't subscribe to getters.
   */
  originalToken: string | null;
  isImpersonating: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  fetchUser: () => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  setUser: (user: User | null) => void;
  setLocale: (locale: Locale) => Promise<void>;
  clearAuth: () => void;
  setHasHydrated: (state: boolean) => void;
  verifyOtp: (otpSession: string, code: string) => Promise<void>;
  resendOtp: (email: string, password: string) => Promise<string>;
  clearOtpSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false,
      authUnreachable: false,
      _hasHydrated: false,
      otpSession: null,
      otpEmail: null,
      originalToken: null,
      isImpersonating: false,

      setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state });
      },

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true });
        try {
          const response = await api.post<ApiResponse<LoginSuccessResponse | OtpLoginResponse>>('/auth/login', credentials);
          const data = response.data.data;

          // Check if OTP is required
          if ('requires_otp' in data && data.requires_otp) {
            set({
              otpSession: data.otp_session,
              otpEmail: credentials.email,
              isLoading: false,
            });
            return; // Don't authenticate yet — caller checks otpSession
          }

          // Normal login (no OTP)
          const loginData = data as LoginSuccessResponse;
          localStorage.setItem('token', loginData.token);
          set({
            user: {
              ...loginData.user,
              feature_flags: loginData.feature_flags,
              // Radiance 261007: the calendar's role preset. /me spreads it onto
              // the user; login has to copy it, or the first /calendar visit
              // after signing in opens (and remembers) the fallback view.
              calendar_presets: loginData.calendar_presets,
              appointment_presets: loginData.appointment_presets,
              report_permissions: loginData.report_permissions,
              report_settle_permissions: loginData.report_settle_permissions,
            },
            token: loginData.token,
            isAuthenticated: true,
            isLoading: false,
            otpSession: null,
            otpEmail: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      verifyOtp: async (otpSession: string, code: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<ApiResponse<LoginSuccessResponse>>('/auth/verify-otp', {
            otp_session: otpSession,
            code,
          });
          const { user, token, feature_flags, calendar_presets, appointment_presets, report_permissions, report_settle_permissions } = response.data.data;

          localStorage.setItem('token', token);
          set({
            user: { ...user, feature_flags, calendar_presets, appointment_presets, report_permissions, report_settle_permissions },
            token,
            isAuthenticated: true,
            isLoading: false,
            otpSession: null,
            otpEmail: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resendOtp: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const response = await api.post<ApiResponse<OtpLoginResponse>>('/auth/login', { email, password });
          const data = response.data.data;
          if ('requires_otp' in data && data.requires_otp) {
            set({
              otpSession: data.otp_session,
              isLoading: false,
            });
            return data.otp_session;
          }
          set({ isLoading: false });
          return '';
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      clearOtpSession: () => set({ otpSession: null, otpEmail: null }),

      // Admin-only (backend gates on `users.impersonate`). Stashes the caller's
      // own token, then adopts the returned target-user session exactly the way
      // login() does — same user/token/feature_flags/appointment_presets/
      // report_permissions assignment.
      startImpersonation: async (userId: string) => {
        const currentToken = get().token || localStorage.getItem('token');
        set({ isLoading: true });
        try {
          const data = await impersonateUser(userId);
          if (currentToken) {
            localStorage.setItem('original_token', currentToken);
          }
          localStorage.setItem('token', data.token);
          set({
            user: {
              ...data.user,
              feature_flags: data.feature_flags,
              appointment_presets: data.appointment_presets,
              report_permissions: data.report_permissions,
              report_settle_permissions: data.report_settle_permissions,
            },
            token: data.token,
            originalToken: currentToken,
            isImpersonating: true,
            isAuthenticated: true,
            isLoading: false,
          });
          // The impersonate payload has no calendar_presets (Radiance 261007:
          // the target's calendar opened on the fallback view and remembered
          // it). /me has the full shape — best-effort, NOT fetchUser(), whose
          // failure path signs out and would drop the stashed admin token.
          try {
            const me = await api.get<ApiResponse<User>>('/auth/me');
            set({ user: me.data.data });
          } catch {
            // Keep the impersonate payload.
          }
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      // Revoke the impersonation token, then swap the admin's own token back in
      // and re-resolve /me. The logout call is best-effort: the impersonation
      // token may already be expired, or the target account deactivated
      // mid-session, and neither should strand the admin.
      stopImpersonation: async () => {
        const original = get().originalToken || localStorage.getItem('original_token');
        set({ isLoading: true });
        try {
          await api.post('/auth/logout');
        } catch {
          // Ignore — token may be expired or the target deactivated.
        }

        if (original) {
          localStorage.setItem('token', original);
        } else {
          localStorage.removeItem('token');
        }
        localStorage.removeItem('original_token');
        set({
          token: original,
          originalToken: null,
          isImpersonating: false,
          isLoading: false,
        });

        await get().fetchUser();
      },

      logout: async () => {
        set({ isLoading: true });
        const original = get().originalToken || localStorage.getItem('original_token');
        try {
          // Revoke whatever token is currently active. While impersonating
          // that's the impersonation token; the admin's own token is still
          // live, so swap it in and revoke it too — otherwise "log out" would
          // leave an orphaned admin session valid on the server.
          await api.post('/auth/logout');
        } catch {
          // Ignore logout errors
        }

        if (original) {
          localStorage.setItem('token', original);
          try {
            await api.post('/auth/logout');
          } catch {
            // Ignore logout errors
          }
        }

        localStorage.removeItem('token');
        localStorage.removeItem('original_token');
        set({
          user: null,
          token: null,
          originalToken: null,
          isImpersonating: false,
          isAuthenticated: false,
          isLoading: false
        });
      },

      fetchUser: async () => {
        const token = get().token || localStorage.getItem('token');
        if (!token) {
          set({ isAuthenticated: false, user: null });
          return;
        }

        set({ isLoading: true, authUnreachable: false });
        // A phone waking the PWA often has no network for the first seconds:
        // retry before deciding anything.
        const delays = [0, 1000, 2000, 4000];
        let lastError: unknown;
        for (const delay of delays) {
          if (delay) await new Promise((r) => setTimeout(r, delay));
          try {
            const response = await api.get<ApiResponse<User>>('/auth/me');
            set({
              user: response.data.data,
              isAuthenticated: true,
              isLoading: false,
              authUnreachable: false,
            });
            return;
          } catch (error) {
            lastError = error;
            if ((error as { response?: { status?: number } })?.response?.status === 401) break;
          }
        }
        {
          const error = lastError;
          // Only a 401 means the session is gone. A phone waking the PWA
          // without signal, a timeout or a 5xx during a deploy used to land
          // here too and throw away a perfectly valid token (Radiance 261001:
          // "it logs me out but notifications still arrive"). Keep the
          // session and let the next call try again.
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status !== 401) {
            set({ isLoading: false, authUnreachable: true });
            return;
          }
          localStorage.removeItem('token');
          localStorage.removeItem('original_token');
          set({
            user: null,
            token: null,
            originalToken: null,
            isImpersonating: false,
            isAuthenticated: false,
            isLoading: false
          });
        }
      },

      forgotPassword: async (data: ForgotPasswordRequest) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/forgot-password', data);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resetPassword: async (data: ResetPasswordRequest) => {
        set({ isLoading: true });
        try {
          await api.post('/auth/reset-password', data);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      setUser: (user: User | null) => set({ user, isAuthenticated: !!user }),

      // Phase 71 I18N-03 — persist the chosen UI locale to the user's own record,
      // mirror it into the NEXT_LOCALE cookie (so the NEXT SSR paint / <html lang>
      // matches), then bump user.locale in client state. The state change makes
      // I18nBootstrap's reconcile useEffect (which watches user?.locale) re-load the
      // catalog and re-render every useTranslations consumer IN PLACE — NO
      // router.refresh() (a no-op for this client-rendered SPA; RESEARCH Pitfall 1).
      setLocale: async (locale: Locale) => {
        await api.patch('/auth/me/locale', { locale });
        writeLocaleCookie(locale);
        const current = get().user;
        if (current) {
          set({ user: { ...current, locale } });
        }
      },

      clearAuth: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('original_token');
        set({
          user: null,
          token: null,
          originalToken: null,
          isImpersonating: false,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ token: state.token, originalToken: state.originalToken }),
      onRehydrateStorage: () => (state) => {
        // `isImpersonating` isn't persisted (it's derived) — restore it from the
        // rehydrated originalToken so a mid-impersonation reload still shows the
        // banner and can be unwound.
        if (state) {
          state.isImpersonating = state.originalToken !== null;
        }
        state?.setHasHydrated(true);
      },
    }
  )
);
